import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SKIP_GLOBAL_ERROR_TOAST } from '@core/constants/http-context.constants';
import { TranslateService } from '@ngx-translate/core';
import { getApiErrorCode } from '@core/utils/api-error-body.utils';
import { formatApiErrorDetail } from '@core/utils/api-error-body.utils';
import { registeredErrorTranslationKey } from '@core/utils/api-error-code-registry';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { ToastService } from '@core/services/toast.service';
import { IDENTITY_SESSION_PATHS } from '@core/constants/auth.constants';
import { IDENTITY_API_BASE_URL } from '@core/constants/api-tokens.constants';
import { ErrorReporter } from '@core/services/error-reporter.service';
import { TOAST_ERROR_LIFE_MS, TOAST_KEY } from '@core/constants/toast.constants';

function isTranslationFile(url: string): boolean {
  return url.includes('/assets/i18n/');
}

/**
 * Identity session endpoints, the `me` probe included. {@link AuthService} owns their failures: a
 * 401 from `me` is the normal "anonymous visitor" answer and must not raise a session-expired toast.
 */
function isIdentitySessionRequest(url: string, identityApiBaseUrl: string): boolean {
  return IDENTITY_SESSION_PATHS.some(path => url === `${identityApiBaseUrl}${path}`);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (isTranslationFile(req.url)) {
    return next(req);
  }

  const messageService = inject(ToastService);
  const translate = inject(TranslateService);
  const language = inject(LanguageService);
  const authService = inject(AuthService);
  const identityApiBaseUrl = inject(IDENTITY_API_BASE_URL);
  const reporter = inject(ErrorReporter);

  const silent = req.context.get(SKIP_GLOBAL_ERROR_TOAST) === true;

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }
      const isSession = isIdentitySessionRequest(req.url, identityApiBaseUrl);
      // A 401 from an Identity session call is the normal "signed out" answer, not a failure.
      if (!(error.status === 401 && isSession)) {
        reporter.report(error, { source: 'http', url: req.url, status: error.status });
      }

      // 401 session handling is outside `silent`: every protected 401 must go through the
      // once-only gate so concurrent failures cannot stack duplicate toasts. `handleUnauthorized`
      // sends the visitor back to Identity with a login intent for the current URL.
      if (error.status === 401 && !isSession && authService.handleUnauthorized()) {
        messageService.clear(TOAST_KEY);
        messageService.show({
          key: TOAST_KEY,
          severity: 'warn',
          summary: translate.instant('common.sessionExpired'),
          detail: translate.instant('common.pleaseLoginAgain'),
          life: TOAST_ERROR_LIFE_MS,
        });
        return throwError(() => error);
      }

      if (!silent) {
        const life = TOAST_ERROR_LIFE_MS;
        if (error.status === 0) {
          messageService.show({
            key: TOAST_KEY,
            severity: 'error',
            summary: translate.instant('common.error'),
            detail: translate.instant('common.networkError'),
            life,
          });
        } else if (error.status === 403) {
          messageService.show({
            key: TOAST_KEY,
            severity: 'error',
            summary: translate.instant('common.accessDenied'),
            detail: translate.instant('common.notAuthorized'),
            life,
          });
        } else if (error.status !== 401) {
          const detail = resolveHttpErrorDetail(error, translate, language.isRTL());

          messageService.show({
            key: TOAST_KEY,
            severity: 'error',
            summary: translate.instant('common.error'),
            detail,
            life,
          });
        }
      }

      return throwError(() => error);
    })
  );
};

function resolveHttpErrorDetail(
  error: HttpErrorResponse,
  translate: TranslateService,
  preferAr: boolean
): string {
  const fallback = translate.instant('common.unexpectedError');
  const raw = formatApiErrorDetail(error.error, fallback, preferAr);
  const code = getApiErrorCode(error);
  if (!code) return raw;
  const i18nKey = registeredErrorTranslationKey(code);
  if (!i18nKey) return raw;
  const localized = translate.instant(i18nKey);
  return localized !== i18nKey ? localized : raw;
}
